import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { HardDriveDownload } from 'lucide-react';
import { EmptyState, PageHeader, SkeletonList } from '@/components/ui';
import { SignListItem } from '@/components/SignListItem';
import { db } from '@/db/schema';
import { useCategoryNames, useSignStates } from '@/hooks/useCatalogQueries';
import { DownloadQueue } from '@/features/downloads/DownloadQueue';
import { StorageMeter } from '@/features/downloads/StorageMeter';
import { BatchDownloadCard } from '@/features/downloads/BatchDownloadCard';
import { OfflineReadinessCard } from '@/features/offline/OfflineReadinessCard';
import type { Sign } from '@/types/catalog';

/**
 * Conteúdo disponível offline e fila de downloads (F8-T08, F12-T10 a T13).
 *
 * A medição de armazenamento e a verificação de prontidão para viagem entram
 * nas FASES 13 e 14.
 */
export function DownloadsPage(): React.ReactElement {
  const offlineSigns = useLiveQuery(async () => {
    const states = await db.localSignState
      .where('offline.status')
      .equals('available')
      .toArray();
    if (states.length === 0) return [];

    const signs = await db.signs.bulkGet(states.map((state) => state.signId));
    return signs.filter((sign): sign is Sign => sign !== undefined);
  }, []);

  const categoryNames = useCategoryNames();
  const ids = useMemo(() => (offlineSigns ?? []).map((sign) => sign.id), [offlineSigns]);
  const states = useSignStates(ids);

  return (
    <>
      <PageHeader
        title="Offline"
        subtitle={offlineSigns ? `${offlineSigns.length} vídeos no dispositivo` : undefined}
        showConnection
      />

      <div className="flex flex-col gap-3">
        <OfflineReadinessCard />
        <DownloadQueue />
        <BatchDownloadCard />
        <StorageMeter />
      </div>

      {offlineSigns === undefined && <SkeletonList rows={5} />}

      {offlineSigns?.length === 0 && (
        <EmptyState
          icon={<HardDriveDownload size={40} aria-hidden />}
          title="Nenhum vídeo salvo ainda"
          description={
            'O glossário, a busca, os favoritos e o progresso já funcionam offline. ' +
            'Use "Baixar glossário completo" abaixo para levar os vídeos junto.'
          }
        />
      )}

      {offlineSigns && offlineSigns.length > 0 && (
        <ul className="flex flex-col pb-4">
          {offlineSigns.map((sign) => (
            <SignListItem
              key={sign.id}
              sign={sign}
              state={states?.get(sign.id)}
              categoryName={categoryNames?.get(sign.categoryId)}
            />
          ))}
        </ul>
      )}
    </>
  );
}
